import {
  GRAPHQL_ERROR_STATUS,
  SCHEMA_MISMATCH_STATUS,
  TQueryResult,
} from '@/shared/redux/api/base-api.types';
import {
  NOTIFICATION_PREFERENCE_CONFLICT_ERROR,
  OPENPLC_ORCHESTRATOR_UNREACHABLE_ERROR,
  PIPELINE_EXECUTION_ALREADY_RUNNING_ERROR,
  isFeatureFlagStillAssignedMessage,
} from '@/shared/redux/api/utils/backend-error-messages';
import { normalizeGraphQLData } from '@/shared/redux/api/utils/normalize-graphql-data';
import { showToastError } from '@/shared/utils/toast';

// Backoffice surfaces the tenant-access denial in its own error boundary.
const SUPPRESSED_MESSAGES = [
  'Unauthorized',
  'not found',
  'Failed to fetch',
  'is not allowed for the given super-tenant',
  // Suppressed so the raw English backend string is not toasted alongside the
  // translated one; trigger-pipeline-executions.tsx matches the same literal.
  PIPELINE_EXECUTION_ALREADY_RUNNING_ERROR,
  // Same reason; notification-preferences.tsx toasts its own translated conflict.
  NOTIFICATION_PREFERENCE_CONFLICT_ERROR,
  // Same reason; upgrade-orchestrator.tsx toasts its own translated message.
  OPENPLC_ORCHESTRATOR_UNREACHABLE_ERROR,
];
const READ_OR_LIST_PERMISSION_DENIAL_PATTERN =
  /\b[a-zA-Z][\w]*:(?:read|list)[A-Za-z0-9_]*\b/;
const BACKEND_PERMISSION_DENIAL_PATTERN =
  /does not have permission to perform action\s+['"]([^'"]+)['"]/i;
const SUPPRESSED_PERMISSION_ACTIONS = ['vault:listSecrets'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const stringifyErrorPart = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value instanceof Error) {
    return value.message;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getStatus = (status: unknown): number | string | undefined =>
  typeof status === 'number' || typeof status === 'string' ? status : undefined;

const isForbiddenStatus = (status?: number | string): boolean =>
  status === 403 || String(status).toUpperCase() === 'FORBIDDEN';

const hasPermissionDenialText = (message: string): boolean =>
  /forbidden|permission|not authorized|http 403/i.test(message);

const getBackendPermissionDenialAction = (
  message: string,
): string | undefined => message.match(BACKEND_PERMISSION_DENIAL_PATTERN)?.[1];

const isReadOrListAction = (action?: string): boolean =>
  /:(?:read|list)[A-Za-z0-9_]*$/.test(action ?? '');

// Shared by isPermissionDeniedError (gating) and shouldSuppressError so a gated
// read can never also toast.
const isReadOrListPermissionDenial = (
  message: string,
  status?: number | string,
): boolean => {
  const backendAction = getBackendPermissionDenialAction(message);
  if (backendAction) {
    return (
      isReadOrListAction(backendAction) ||
      SUPPRESSED_PERMISSION_ACTIONS.includes(backendAction)
    );
  }

  // Needs a read/list op-name plus denial context; a context-free 403 is
  // excluded so write denials past a missing gate still toast.
  return (
    READ_OR_LIST_PERMISSION_DENIAL_PATTERN.test(message) &&
    (isForbiddenStatus(status) || hasPermissionDenialText(message))
  );
};

const getErrorText = (error: unknown): string => {
  if (error instanceof Error || typeof error === 'string') {
    return stringifyErrorPart(error);
  }

  if (!isRecord(error)) {
    return stringifyErrorPart(error);
  }

  return [
    error.message,
    error.error,
    error.statusText,
    error.status,
    error.data,
  ]
    .map(stringifyErrorPart)
    .filter(Boolean)
    .join(' ');
};

export const isPermissionDeniedError = (error: unknown): boolean => {
  const status = isRecord(error) ? getStatus(error.status) : undefined;
  const message = getErrorText(error);

  return isReadOrListPermissionDenial(message, status);
};

/**
 * Checks if an error message should be suppressed from user notifications
 */
export const shouldSuppressError = (
  message: string,
  status?: number | string,
): boolean => {
  if (
    SUPPRESSED_MESSAGES.some((suppressedMessage) =>
      message.includes(suppressedMessage),
    )
  ) {
    return true;
  }

  // The catalog delete modal renders this refusal itself, with the cascade option
  // that answers it; delete-catalog-flag-modal.tsx matches the same pattern.
  if (isFeatureFlagStillAssignedMessage(message)) {
    return true;
  }

  return isReadOrListPermissionDenial(message, status);
};

export const extractUserFriendlyErrorMessage = (errorMsg: string): string => {
  const technicalPatterns = [
    /https?:\/\/\S+/g, // URLs
    /\{.*\}/g, // JSON objects
    /\[.*\]/g, // Arrays
    /.*validation error.*/gi, // Validation errors
    /.*type=\w+.*/g, // Type references
    /.*input_value=.*/g, // Input values
    /.*input_type=.*/g, // Input types
    /.*line:\d+.*/gi, // Line numbers
    /.*column:\d+.*/gi, // Column numbers
    /.*path:.*/gi, // Path references
    /[a-zA-Z0-9_]+\(\)/g, // Function calls
    /[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+/g, // Property access
  ];

  let cleanedMsg = errorMsg;

  // Strip all technical patterns
  for (const pattern of technicalPatterns) {
    cleanedMsg = cleanedMsg.replace(pattern, '');
  }

  return cleanedMsg
    .replace(/^Error:\s*/i, '') // Remove "Error:" prefix
    .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
    .trim();
};

/**
 * Shows a toast error if the message shouldn't be suppressed
 */
export const showErrorIfNotSuppressed = (
  errorMessage: string,
  status?: number | string,
): void => {
  const userFriendlyMessage = extractUserFriendlyErrorMessage(errorMessage);

  if (
    userFriendlyMessage &&
    !shouldSuppressError(userFriendlyMessage, status)
  ) {
    showToastError(userFriendlyMessage);
  }
};

/**
 * Checks if the error data contains meaningful non-null values
 */
export const hasValidErrorData = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  return !Object.values(data).every((value) => value === null);
};

/**
 * Handles error responses and shows appropriate user notifications
 */
export const handleError = (result: TQueryResult): TQueryResult | undefined => {
  // Schema mismatches are reported to Sentry/console at the validation site.
  // We deliberately skip the toast and the error.data recovery here — recovering
  // would route the malformed payload back to the component, defeating the
  // whole point of failing the query.
  if (result?.error?.status === SCHEMA_MISMATCH_STATUS) {
    return undefined;
  }

  const errorMessage = result?.error?.message || 'Unknown error';
  showErrorIfNotSuppressed(errorMessage, result?.error?.status);

  // Return normalized data if it contains valid non-null values
  if (
    result?.error?.status === GRAPHQL_ERROR_STATUS &&
    result?.error?.data &&
    hasValidErrorData(result?.error.data)
  ) {
    return { data: normalizeGraphQLData(result?.error.data) };
  }

  return undefined;
};

/**
 * Handles network and fetch errors
 */
export const handleFetchError = (error: unknown): TQueryResult => {
  const errorMessage =
    error instanceof Error ? error.message : 'Unknown network error';

  showErrorIfNotSuppressed(errorMessage);

  return {
    error: {
      status: 'FETCH_ERROR',
      error: errorMessage,
    },
  };
};
